import { usePlayerEngine, usePlayer } from "@utils/player/core/usePlayer";
import { useEffect, useState } from "react";
import LBox from "../LBox";
import LPressable from "@components/LPressable";
import { useAuthSheet } from "@components/modals/AuthModal/useAuthSheet";
import LIcon from "@components/LIcon";
import { useSharedValue } from "react-native-reanimated";
import {
  usePlayerControls,
  useSeekable,
  useActiveSongId,
  useActiveSongDuration,
  useIsBuffering,
} from "@utils/player";

import { selectActiveQueueItemId } from "@utils/player/core/selectors";
import { TimeLabel } from "./TimeLabel";
import { ProgressBar } from "./ProgressBar";
import { ProgressBarBuffering } from "./ProgressBarBuffering";
import { Platform } from "react-native";

type ProgressForQueueItemProps = {
  activeQueueItemId: string | null;
};

const ProgressForQueueItem = ({
  activeQueueItemId,
}: ProgressForQueueItemProps) => {
  const { getProgressPositionForQueueItem } = usePlayerEngine().progress;

  // PERFORMANCE: Use primitive hooks instead of useActiveSong to avoid re-renders on queue switch
  const { endSeek, seekPreview: seek, startSeek } = usePlayerControls();
  const seekable = useSeekable(); // Only updates when ads change
  const activeSongId = useActiveSongId(); // Primitive - only re-renders when song ID changes
  const duration = useActiveSongDuration(); // Primitive - only re-renders when duration changes
  const isBuffering = useIsBuffering();
  const playbackLimitConfig = usePlayer((s) => s.playbackLimitConfig);
  const openAuthSheet = useAuthSheet();

  const [isSeeking, setIsSeeking] = useState(false);
  const [, setSeekingPosition] = useState(0);

  // This component is keyed by queue-item identity. Queue actions seed the
  // destination target before publishing its queue ID, so a switch constructs
  // a fresh shared value with the new item's position before it can paint.
  const uiProgressValue = useSharedValue(
    getProgressPositionForQueueItem(activeQueueItemId) ?? 0,
  );

  // Sync the position owned by the displayed queue item. AudioPlayer keeps an
  // optimistic seek position stable until native acknowledges SEEK_COMPLETE,
  // so this needs no time-based suppression window.
  // This reads from progressState shared values (100ms updates, NO React re-renders)
  useEffect(() => {
    const sync = () => {
      if (!isSeeking) {
        uiProgressValue.value =
          getProgressPositionForQueueItem(activeQueueItemId) ?? 0;
      }
    };

    // Sync every 100ms to match progress update frequency
    const interval = setInterval(sync, 100);
    return () => clearInterval(interval);
  }, [activeQueueItemId, isSeeking, uiProgressValue]);

  const handleEndSeek = async (pos: number) => {
    await endSeek(pos);
    setIsSeeking(false);
  };

  // duration is already from useActiveSongDuration()

  return (
    <LBox flexDirection="row" alignItems="center" justifyContent="center">
      <LBox
        minWidth={40}
        maxWidth={Platform.OS === "web" ? 40 : undefined}
        pl="s1"
      >
        <TimeLabel positionValue={uiProgressValue} />
      </LBox>
      <LBox flexGrow={1} marginHorizontal="s2" justifyContent="center">
        <LBox>
          <ProgressBar
            seekable={seekable}
            duration={duration}
            setIsSeeking={setIsSeeking}
            startSeek={startSeek}
            endSeek={handleEndSeek}
            seek={seek}
            playbackLimitConfig={playbackLimitConfig}
            setSeekingPosition={setSeekingPosition}
            progressValue={uiProgressValue}
          />
          {isBuffering && (
            <LBox
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              justifyContent="center"
              pointerEvents="none"
            >
              <ProgressBarBuffering
                songId={activeSongId ?? undefined}
                duration={duration}
                progressValue={uiProgressValue}
              />
            </LBox>
          )}
        </LBox>
        {playbackLimitConfig.enabled && (
          <LBox
            width={`${((duration - playbackLimitConfig.limitSeconds) / duration) * 100}%`}
            height={4}
            position="absolute"
            right={0}
            justifyContent="center"
            backgroundColor="white"
            pointerEvents="none"
          >
            <LPressable
              sentryEventName="preview_button"
              onPress={openAuthSheet}
              style={{
                width: 16,
                height: 16,
                backgroundColor: "#909090",
                alignItems: "center",
                justifyContent: "center",
                borderWidth: 1,
                borderColor: "#ffffff",
                borderRadius: 5,
                pointerEvents: "auto",
              }}
            >
              <LIcon name="lock_locked" size={10} />
            </LPressable>
          </LBox>
        )}
      </LBox>
      <LBox
        minWidth={40}
        maxWidth={Platform.OS === "web" ? 40 : undefined}
        alignItems="flex-end"
      >
        <TimeLabel positionValue={duration} />
      </LBox>
    </LBox>
  );
};

const Progress = () => {
  const activeQueueItemId = usePlayer(selectActiveQueueItemId);

  return (
    <ProgressForQueueItem
      key={activeQueueItemId ?? "no-active-queue-item"}
      activeQueueItemId={activeQueueItemId}
    />
  );
};

export default Progress;
